import { inputBaseClasses } from '@mui/material/InputBase';
import type { Components } from '@mui/material/styles';

import type { Theme } from '../theme/types';

export const MuiInputBase = {
  styleOverrides: {
    root: {
      '--Input-borderRadius': '8px',
      '--Input-paddingBlock': 0,
      '--Input-paddingInline': '12px',
      '--Input-minHeight': '40px',
      borderRadius: 'var(--Input-borderRadius)',
      paddingBlock: 'var(--Input-paddingBlock)',
      paddingInline: 'var(--Input-paddingInline)',
      minHeight: 'var(--Input-minHeight)',
    },
    input: {
      alignItems: 'center',
      alignSelf: 'stretch',
      display: 'inline-flex !important', // Fix flicker
      fontSize: 'var(--Input-fontSize, 1rem)',
      '&::placeholder': { color: 'var(--mui-palette-text-secondary)', opacity: 1 },
      '&:-webkit-autofill': {
        borderRadius: 'inherit',
        marginBlock: 'calc(var(--Input-paddingBlock) * -1)',
        marginInline: 'calc(var(--Input-paddingInline) * -1)',
        paddingBlock: 'var(--Input-paddingBlock)',
        paddingInline: 'var(--Input-paddingInline)',
      },
    },
    multiline: { '--Input-paddingBlock': '12px' },
    sizeSmall: { '--Input-fontSize': '0.875rem', '--Input-paddingInline': '8px', '--Input-minHeight': '32px' },
  },
} satisfies Components<Theme>['MuiInputBase'];

export const MuiFilledInput = {
  defaultProps: {
    disableUnderline: true,
  },
  styleOverrides: {
    root: {
      borderRadius: 'var(--Input-borderRadius)',
      '&::before': { display: 'none' },
      '&::after': { display: 'none' },
      'label + &': { marginTop: '8px' },
    },
    input: {
      height: 'auto',
      padding: 0,
      [`label[data-shrink=false]+.${inputBaseClasses.formControl} &`]: {
        '&::placeholder': { opacity: '1 !important' },
      },
      '&:-webkit-autofill': {
        marginInline: 'calc(var(--Input-paddingInline) * -1)',
        paddingInline: 'var(--Input-paddingInline)',
      },
    },
  },
} satisfies Components<Theme>['MuiFilledInput'];

export const MuiInput = {
  defaultProps: {
    disableUnderline: true,
  },
  styleOverrides: {
    root: {
      '&::before': { borderBottomColor: 'var(--mui-palette-OutlinedInput-border)' },
      'label + &': { marginTop: '8px' },
    },
    input: {
      height: 'auto',
      padding: 0,
      [`label[data-shrink=false]+.${inputBaseClasses.formControl} &`]: {
        '&::placeholder': { opacity: '1 !important' },
      },
      '&:-webkit-autofill': {
        marginInline: 'calc(var(--Input-paddingInline) * -1)',
        paddingInline: 'var(--Input-paddingInline)',
      },
    },
  },
} satisfies Components<Theme>['MuiInput'];

export const MuiInputLabel = {
  defaultProps: {
    shrink: true,
  },
  styleOverrides: {
    root: {
      fontSize: 'var(--mui-fontSize-sm)',
      fontWeight: 500,
      maxWidth: '100%',
      position: 'static',
      transform: 'none',
    },
  },
} satisfies Components<Theme>['MuiInputLabel'];

export const MuiFormLabel = {
  styleOverrides: {
    root: {
      color: 'var(--mui-palette-text-primary)',
      fontSize: 'var(--mui-fontSize-sm)',
      fontWeight: 500,
    },
  },
} satisfies Components<Theme>['MuiFormLabel'];

export const MuiFormControlLabel = {
  defaultProps: {
    slotProps: { typography: { variant: 'subtitle2' } },
  },
  styleOverrides: {
    root: {
      gap: '8px',
      margin: 0,
    },
    label: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
  },
} satisfies Components<Theme>['MuiFormControlLabel'];

export const MuiFormGroup = {
  styleOverrides: {
    root: {
      gap: '16px',
    },
  },
} satisfies Components<Theme>['MuiFormGroup'];

export const MuiTextField = {
  defaultProps: {
    InputLabelProps: { shrink: true },
  },
} satisfies Components<Theme>['MuiTextField'];

export const MuiTimelineConnector = {
  styleOverrides: {
    root: {
      backgroundColor: 'var(--mui-palette-divider)',
    },
  },
} satisfies Components<Theme>['MuiTimelineConnector'];
